import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus, Star, X } from 'lucide-react';
import { rupees } from '@/lib/shop';

/* ------------------------------------------------------------ PriceTag */

const PRICE_SIZES = {
  sm: { price: 'text-base', mrp: 'text-xs', off: 'text-xs' },
  md: { price: 'text-lg', mrp: 'text-sm', off: 'text-sm' },
  lg: { price: 'text-[28px] leading-none', mrp: 'text-base', off: 'text-base' },
};

/** Selling price, struck MRP and "% off" in green — the marketplace price line. */
export const PriceTag = ({ price, mrp, discount, size = 'sm', className = '' }) => {
  const s = PRICE_SIZES[size] || PRICE_SIZES.sm;
  const off = discount ?? (mrp > price ? Math.floor(((mrp - price) / mrp) * 100) : 0);
  return (
    <div className={`tabular flex flex-wrap items-baseline gap-x-2 gap-y-0.5 ${className}`}>
      <span className={`${s.price} font-bold text-ink-900`}>{rupees(price)}</span>
      {mrp > price && <span className={`${s.mrp} text-ink-400 line-through`}>{rupees(mrp)}</span>}
      {off > 0 && <span className={`${s.off} font-semibold text-success`}>{off}% off</span>}
    </div>
  );
};

/* ---------------------------------------------------------- RatingPill */

/** Green "4.2 ★" pill; amber below 3, red below 2 — the convention shoppers read at a glance. */
export const RatingPill = ({ value, count, className = '' }) => {
  const v = Number(value) || 0;
  if (!v) return null;
  const tone = v >= 3 ? 'bg-success' : v >= 2 ? 'bg-warning' : 'bg-danger';
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className={`tabular inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-bold text-white ${tone}`}>
        {v.toFixed(1)}
        <Star className="h-3 w-3 fill-white" aria-hidden="true" />
      </span>
      {count > 0 && <span className="tabular text-xs text-ink-500">({count.toLocaleString('en-IN')})</span>}
    </span>
  );
};

/* --------------------------------------------------------------- Stars */

export const Stars = ({ value = 0, size = 'h-4 w-4', onChange, label = 'Rating' }) => (
  <div className="inline-flex items-center gap-0.5" role={onChange ? 'radiogroup' : 'img'} aria-label={onChange ? label : `${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = n <= Math.round(value);
      const icon = <Star className={`${size} ${filled ? 'fill-amber-400 text-amber-400' : 'text-ink-200'}`} aria-hidden="true" />;
      return onChange ? (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={n === value}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
          onClick={() => onChange(n)}
          className="lk-focus rounded p-1 transition-transform hover:scale-110"
        >
          {icon}
        </button>
      ) : (
        <span key={n}>{icon}</span>
      );
    })}
  </div>
);

/* ---------------------------------------------------------- QtyStepper */

export const QtyStepper = ({ value, min = 1, max = 10, onChange, disabled, size = 'md' }) => {
  const h = size === 'sm' ? 'h-9' : 'h-11';
  const w = size === 'sm' ? 'w-9' : 'w-11';
  return (
    <div className={`inline-flex items-center overflow-hidden rounded-xl border border-ink-200 bg-white ${disabled ? 'opacity-60' : ''}`}>
      <button
        type="button"
        aria-label="Decrease quantity"
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
        className={`lk-focus flex ${h} ${w} items-center justify-center text-ink-700 transition hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-ink-200`}
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="tabular min-w-[2.25rem] text-center text-sm font-semibold text-ink-900" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        aria-label="Increase quantity"
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
        className={`lk-focus flex ${h} ${w} items-center justify-center text-ink-700 transition hover:bg-surface-muted disabled:cursor-not-allowed disabled:text-ink-200`}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
};

/* --------------------------------------------------------------- Sheet */

/**
 * Bottom sheet on phones, centred dialog from `sm` up. Escape and the backdrop
 * close it; the page behind stops scrolling while it is open.
 */
export const Sheet = ({ open, onClose, title, children, footer, wide = false }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/45"
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            initial={{ y: 40 }}
            animate={{ y: 0 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.2 }}
            className={`relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl ${
              wide ? 'sm:max-w-2xl' : 'sm:max-w-md'
            }`}
          >
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-ink-200/70 px-5 py-4">
              <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
              <button type="button" onClick={onClose} aria-label="Close" className="lk-icon-btn h-9 w-9 border-0">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">{children}</div>
            {footer && <div className="shrink-0 border-t border-ink-200/70 bg-white px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

/* ---------------------------------------------------------- StatusPill */

const TONES = {
  success: 'bg-success-tint text-success',
  warning: 'bg-warning-tint text-warning',
  danger: 'bg-danger-tint text-danger',
  info: 'bg-brand-tint text-brand-secondary',
  muted: 'bg-surface-sunken text-ink-700',
};

export const StatusPill = ({ tone = 'muted', children, className = '' }) => (
  <span className={`lk-badge ${TONES[tone] || TONES.muted} ${className}`}>{children}</span>
);

/* ---------------------------------------------------------- Pagination */

export const Pagination = ({ page, lastPage, onChange }) => {
  if (!lastPage || lastPage <= 1) return null;
  const pages = [];
  const from = Math.max(1, page - 2);
  const to = Math.min(lastPage, page + 2);
  if (from > 1) pages.push(1, from > 2 ? '…l' : null);
  for (let p = from; p <= to; p += 1) pages.push(p);
  if (to < lastPage) pages.push(to < lastPage - 1 ? '…r' : null, lastPage);
  const btn = 'lk-focus flex h-10 min-w-[2.5rem] items-center justify-center rounded-xl px-3 text-sm font-semibold transition';
  return (
    <nav aria-label="Pages" className="mt-8 flex flex-wrap items-center justify-center gap-1.5">
      <button type="button" disabled={page <= 1} onClick={() => onChange(page - 1)} className={`${btn} border border-ink-200 bg-white text-ink-700 hover:bg-surface-muted disabled:opacity-40`}>
        Previous
      </button>
      {pages.filter(Boolean).map((p) =>
        typeof p === 'string' ? (
          <span key={p} className="px-1 text-ink-400">…</span>
        ) : (
          <button
            key={p}
            type="button"
            aria-current={p === page ? 'page' : undefined}
            onClick={() => onChange(p)}
            className={`${btn} ${p === page ? 'bg-brand-primary text-white' : 'border border-ink-200 bg-white text-ink-700 hover:bg-surface-muted'}`}
          >
            {p}
          </button>
        )
      )}
      <button type="button" disabled={page >= lastPage} onClick={() => onChange(page + 1)} className={`${btn} border border-ink-200 bg-white text-ink-700 hover:bg-surface-muted disabled:opacity-40`}>
        Next
      </button>
    </nav>
  );
};
